'use client';

import { useEffect, useRef, useState } from 'react';
import { hydrateGeoDistrictIfNeeded } from '@/lib/geo/hydrate-district';
import { GEO_UPDATED_EVENT, readGeoCache } from '@/lib/geo/storage';

export interface SavedLocation {
  stateId: string | null;
  stateName: string | null;
  districtId: string | null;
  districtName: string | null;
  pincode: string | null;
  lat: number | null;
  lng: number | null;
}

function cacheToSavedLocation(): SavedLocation | null {
  const cached = readGeoCache();
  if (!cached) return null;
  if (!cached.stateId && !cached.districtId && !cached.pincode) return null;
  return {
    stateId: cached.stateId,
    stateName: cached.stateName,
    districtId: cached.districtId,
    districtName: cached.districtName,
    pincode: cached.pincode ?? null,
    lat: cached.lat ?? null,
    lng: cached.lng ?? null,
  };
}

/**
 * Reactive view of the site-wide geo cache (state / district / pincode).
 * Updates when GlobalLocationPrompt or dealer locator writes a new detection.
 *
 * Initialised from localStorage on the client immediately so forms that mount
 * after the location prompt (sales callback sheet, contact step-2, etc.) get
 * values on the first paint instead of waiting a tick and missing the apply.
 */
export function useSavedLocation(): SavedLocation | null {
  const [location, setLocation] = useState<SavedLocation | null>(() => {
    if (typeof window === 'undefined') return null;
    return cacheToSavedLocation();
  });

  useEffect(() => {
    const sync = () => setLocation(cacheToSavedLocation());
    sync();
    // Upgrade partial caches (state without district) using raw Google names.
    void hydrateGeoDistrictIfNeeded().then(sync);
    window.addEventListener(GEO_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(GEO_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return location;
}

/**
 * Prefills forms from the shared geo cache.
 *
 * - Runs whenever the cache gains new fields (e.g. state first, then pincode).
 * - Callers must only write into empty fields so user edits are never overwritten.
 * - `resetKey` (e.g. active form variant id) clears the applied fingerprint so a
 *   newly shown form variant can receive the same location data.
 */
export function usePrefillSavedLocation(
  apply: (location: SavedLocation) => void,
  enabled = true,
  resetKey?: string | number | null,
): void {
  const appliedFingerprintRef = useRef<string | null>(null);
  const lastResetKeyRef = useRef(resetKey);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const location = useSavedLocation();

  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      appliedFingerprintRef.current = null;
    }

    if (!enabled || !location) return;
    if (!location.stateId && !location.districtId && !location.pincode) return;

    const fingerprint = [
      resetKey ?? '',
      location.stateId ?? '',
      location.districtId ?? '',
      location.pincode ?? '',
    ].join('|');

    if (appliedFingerprintRef.current === fingerprint) return;
    appliedFingerprintRef.current = fingerprint;
    applyRef.current(location);
  }, [enabled, location, resetKey]);
}
